/**
 * Browse bookish events by location.
 *
 * Replaces the old "Browse all available events" popup, which did not read the
 * events table at all: it queried `posts` for rows carrying an `event_data`
 * blob, capped at 50, newest-first, with no upcoming filter. So the festivals
 * seeded into profile_events and everything imported through the admin CSV
 * importer -- precisely the events that have real locations -- never appeared
 * in it. This reads profile_events.
 *
 * ── The shape of it ───────────────────────────────────────────────────────
 *
 * ONE popup, four screens, one back arrow. Not four sibling modals: the block
 * this replaces navigated by closing one modal and opening another, so there
 * was no way back from Browse to where you started.
 *
 * The step machine follows JournalEntryFlow in ProfileBlocks.jsx: a single
 * useState holding a STRING, every piece of state hoisted flat above it, named
 * transitions rather than next()/prev(), and `{step === 'x' && ...}` fragments
 * inside one shell.
 *
 *   locations -> regions -> list -> detail
 *
 * `listSource` records how the list was reached, because searching from the
 * first screen jumps straight past the middle one and the arrow has to go back
 * where you actually came from.
 *
 * ── Where the numbers come from ───────────────────────────────────────────
 *
 * event_country_counts() and event_region_counts() (migration
 * 20260918000000_event_locations.sql). Counting in the browser is not an
 * option: Supabase caps any response at 1,000 rows, so a client-side tally
 * counts whatever the first thousand rows happened to be.
 *
 * Those functions also decide WHICH locations exist. A country with no
 * upcoming events is simply absent from the result, which is what makes "if we
 * don't have events in Ireland, Ireland doesn't show up" true in the database
 * rather than in a filter here that could get it wrong.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import { useIsMobile } from '../../hooks/useReaderCorner';
import { onlyUpcoming, formatEventDate, eventDayOfMonth } from '../../lib/eventDates';
import { excludeRsvpCopies } from '../../lib/eventCopies';

// How many events a country needs before the region screen is worth a tap.
//
// Measured, not guessed: of the seeded events still upcoming, the US has 35
// spread across 28 states and Canada has 15 across 8 provinces. A "Select a
// state" list of 28 rows that nearly all read "1" is WORSE than the flat list
// it replaces -- it costs an extra tap to reach a single event. Below either
// threshold the flow goes country -> list directly.
//
// Raise these once there are enough events for the middle screen to earn its
// place.
const REGION_SCREEN_MIN_EVENTS  = 12;
const REGION_SCREEN_MIN_REGIONS = 4;

const COUNTRY_CHIPS_SHOWN = 10;  // then "See more locations"
const REGIONS_SHOWN       = 8;   // then "More regions"
const PAGE                = 20;  // event list page size, well under the 1,000 cap

// Columns the screens actually use. Not select('*') -- `description` is long
// and this list can be twenty rows deep.
const EVENT_COLS =
  'id, name, date, end_date, time, venue, address, city, region, country, country_code, ' +
  'description, ticket_link, event_url, ticket_cost, emoji, color';

/**
 * A flag from a two-letter country code, with no list of countries anywhere.
 *
 * Regional indicator symbols sit at U+1F1E6..U+1F1FF in the same order as
 * A..Z, so "gb" is just two characters of arithmetic. Shipping a country list
 * to draw ten flags would be a list to maintain forever.
 *
 * Returns '' for anything that is not two letters, so a missing or odd code
 * renders nothing rather than tofu boxes.
 */
export function flagEmoji(code) {
  if (!code || typeof code !== 'string') return '';
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Mirrors useDebounce in ProfileBlocks.jsx:547, which is not exported. */
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Turn what a reader typed into a safe `ilike` value.
 *
 * Every rule here was measured against a real PostgREST 12.2.3 in front of a
 * real Postgres, because two layers process this string and reasoning about
 * them gets it wrong:
 *
 *   1. PostgREST parses the filter, and inside a quoted value its parser
 *      consumes ONE backslash layer.
 *   2. Postgres LIKE then wants \% and \_ for a literal % or _.
 *
 * So a literal percent has to leave here as `\\%`, and a literal backslash as
 * `\\\\`. A single backslash looks right and is not: `"%\%%"` reaches Postgres
 * as `%%%` and matches every row.
 *
 * THE QUOTES ARE NOT DECORATION. An unquoted value containing a comma splits
 * the `or=(...)` list and PostgREST answers **400**. "Books, Beer" and
 * "Bath, Somerset" are exactly what someone types into a box captioned
 * "Search events or locations", so unquoted, the search box fails on ordinary
 * input.
 *
 * `*` is dropped rather than escaped. PostgREST maps `*` to `%` BEFORE Postgres
 * sees the pattern, so a literal asterisk cannot be expressed through ilike at
 * all; escaping it yields `\%`, which searches for a literal percent sign and
 * finds nothing. Dropping it makes "Star*" search for "Star", which is what a
 * search box should do.
 */
export function searchPattern(raw) {
  const escaped = raw
    .replace(/\*/g, '')
    .replace(/\\/g, '\\\\\\\\')
    .replace(/%/g, '\\\\%')
    .replace(/_/g, '\\\\_')
    .replace(/"/g, '\\"');
  return `"%${escaped}%"`;
}

// ── The date tile ────────────────────────────────────────────────────────────
// The little month-over-day square on the left of every event row. One
// component because it appears on three of the four screens, and built on
// eventDates so it cannot drift from the rest of the app -- those helpers parse
// at noon rather than midnight, which is why an event never renders as two
// different days on two different cards.
function DateTile({ date }) {
  const month = formatEventDate(date, { month: 'short' }, '').toUpperCase();
  const day   = eventDayOfMonth(date);
  if (!month && !day) return null;
  return (
    <div style={{
      width: 44, flexShrink: 0, borderRadius: 'var(--radius-10)',
      border: '1px solid var(--brand-sand)', background: 'var(--brand-white)',
      textAlign: 'center', padding: '4px 0', lineHeight: 1.1,
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: 'var(--brand-rust)' }}>{month}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)' }}>{day}</div>
    </div>
  );
}

/** "Los Angeles, CA" — whatever of city/region/country we actually have. */
function placeLine(ev) {
  return [ev.city || ev.venue, ev.region, ev.country].filter(Boolean).join(', ');
}

// ── A row in a list of locations ─────────────────────────────────────────────
function LocationRow({ label, flag, count, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--space-10)',
      padding: '13px 4px', background: 'none', border: 'none',
      borderBottom: '1px solid var(--neutral-100)', cursor: 'pointer', textAlign: 'left',
    }}>
      {flag && <span style={{ fontSize: 'var(--font-size-lg)' }}>{flag}</span>}
      <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)' }}>{label}</span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>{count}</span>
      <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>›</span>
    </button>
  );
}

export default function EventsBrowser({ onClose, onHostEvent, onRsvpAdded }) {
  const isMobile = useIsMobile();

  // ── step machine ──────────────────────────────────────────────────────────
  const [step, setStep] = useState('locations');
  // How the list was reached, so the back arrow returns where you came from.
  // 'region' | 'country' | 'search'
  const [listSource, setListSource] = useState('region');

  const [country, setCountry]   = useState(null);  // { country, country_code }
  const [region,  setRegion]    = useState(null);  // string, or null for "All"
  const [viewEvent, setViewEvent] = useState(null);

  // ── screen 1 ──────────────────────────────────────────────────────────────
  const [countries,     setCountries]     = useState([]);
  const [countriesErr,  setCountriesErr]  = useState(null);
  const [countriesLoad, setCountriesLoad] = useState(true);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [popular, setPopular] = useState([]);

  // ── screen 2 ──────────────────────────────────────────────────────────────
  const [regions,    setRegions]    = useState([]);
  const [regionsErr, setRegionsErr] = useState(null);
  const [regionsLoad, setRegionsLoad] = useState(false);
  const [showAllRegions, setShowAllRegions] = useState(false);

  // ── screen 3 ──────────────────────────────────────────────────────────────
  const [events,     setEvents]     = useState([]);
  const [eventsErr,  setEventsErr]  = useState(null);
  const [eventsLoad, setEventsLoad] = useState(false);
  const [exhausted,  setExhausted]  = useState(false);
  const [total,      setTotal]      = useState(null);   // null = not counted, never 0
  // Refs, not state: an offset held in state lags a render by one tick, and
  // that fetches the same page twice.
  const offsetRef    = useRef(0);
  const loadingRef   = useRef(false);
  // Which load is current. Bumped by every load, checked after the await, so a
  // response that arrives for a list nobody is looking at any more is dropped.
  const loadIdRef    = useRef(0);
  const sentinelRef  = useRef(null);

  // ── search ────────────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const debouncedQuery    = useDebounce(query, 400);
  const searchIdRef       = useRef(0);

  // ── rsvp ──────────────────────────────────────────────────────────────────
  const [userId, setUserId]   = useState(null);
  const [myRsvps, setMyRsvps] = useState({});   // { [profile_event_id]: 'going' | 'interested' }
  const [rsvping, setRsvping] = useState(null);

  useEffect(() => { getCurrentUser().then(u => setUserId(u?.id || null)); }, []);

  // ── the shell: escape, and a scroll lock that restores what it found ──────
  // Copied from SupportJar.jsx:92-99, the only modal in the app that does both.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // ── screen 1 data ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setCountriesLoad(true);
      const { data, error } = await supabase.rpc('event_country_counts');
      // "we could not load this" and "there is nothing here" must not render
      // the same, or an outage reads as an empty platform.
      if (error) { setCountriesErr(error.message); setCountries([]); }
      else       { setCountriesErr(null); setCountries(data || []); }
      setCountriesLoad(false);
    })();
  }, []);

  // "Popular this month" — upcoming events inside the current calendar month,
  // rotated by the day of the month so the five on show change daily. No
  // popularity data is needed and none is implied: it is a sampler.
  useEffect(() => {
    (async () => {
      const now       = new Date();
      const monthEnd  = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const endStr    = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth() + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;
      const offset    = (now.getDate() - 1) * 2;
      const { data, error } = await onlyUpcoming(
        excludeRsvpCopies(
          supabase.from('profile_events')
            .select(EVENT_COLS)
            .lte('date', endStr)))
        .order('id', { ascending: true })
        .range(offset, offset + 4);
      if (!error) setPopular(data || []);
      else console.error('EventsBrowser: popular-this-month failed:', error.message);
    })();
  }, []);

  // ── screen 2 data ─────────────────────────────────────────────────────────
  const openCountry = useCallback(async (row) => {
    setCountry(row);
    setRegion(null);
    setShowAllRegions(false);
    setRegionsLoad(true);
    setStep('regions');

    const { data, error } = await supabase.rpc('event_region_counts', { p_country: row.country });
    setRegionsLoad(false);
    if (error) { setRegionsErr(error.message); setRegions([]); return; }
    setRegionsErr(null);
    const rows = data || [];
    setRegions(rows);

    // Skip the middle screen when it would be a list of 1s. See the constants.
    if (row.n < REGION_SCREEN_MIN_EVENTS || rows.length < REGION_SCREEN_MIN_REGIONS) {
      setListSource('country');
      setStep('list');
    }
  }, []);

  // ── screen 3 data ─────────────────────────────────────────────────────────
  const buildListQuery = useCallback(() => {
    // Never count or list a reader's RSVP copy as an event of its own. The
    // helper carries the whole rule -- `source_event_id is null` alone is
    // blind to every copy made before that column existed, which on the live
    // data was all of them.
    let q = excludeRsvpCopies(
      supabase.from('profile_events').select(EVENT_COLS, { count: 'exact' }));

    if (listSource === 'search') {
      const term = searchPattern(debouncedQuery.trim());
      // Name AND location, unlike the box this replaces, whose placeholder said
      // "Search by name, location..." while it only ever matched the title.
      q = q.or(`name.ilike.${term},city.ilike.${term},region.ilike.${term},country.ilike.${term},venue.ilike.${term}`);
    } else {
      // `country` null is the "Everywhere else" bucket -- events the geocoder
      // could not place. They stay reachable rather than being dropped.
      q = country?.country ? q.eq('country', country.country) : q.is('country', null);
      if (region) q = q.eq('region', region);
    }
    // .order('id') is a tiebreaker, not decoration: events sharing a date would
    // otherwise be skipped or repeated across pages.
    return onlyUpcoming(q).order('date', { ascending: true }).order('id', { ascending: true });
  }, [listSource, debouncedQuery, country, region]);

  const loadPage = useCallback(async (reset) => {
    // A RESET must never be dropped, only an extra page. `loadingRef` used to
    // guard both, so tapping back and picking another region while the first
    // page was still in flight threw the new region's load away -- leaving
    // California's events sitting under a heading that said Texas, with no
    // error and nothing to retry.
    //
    // Instead every load takes a ticket, and a reset invalidates the ones
    // already out. The stale response still arrives and is discarded, which
    // is the same stale-response guard JournalEntryFlow uses for its book
    // search (its searchIdRef, in ProfileBlocks.jsx).
    if (!reset && (loadingRef.current || exhausted)) return;

    const myLoad = ++loadIdRef.current;
    loadingRef.current = true;
    if (reset) { offsetRef.current = 0; setExhausted(false); setEvents([]); setTotal(null); }
    setEventsLoad(true);

    const from = offsetRef.current;
    const { data, error, count } = await buildListQuery().range(from, from + PAGE - 1);

    // Somebody has asked for a different list since this one was sent.
    // Touching state now would paste these rows under the new heading.
    if (loadIdRef.current !== myLoad) return;

    setEventsLoad(false);
    loadingRef.current = false;

    if (error) { setEventsErr(error.message); setExhausted(true); return; }
    setEventsErr(null);
    // Never `count || 0`: a count that did not come back is not zero events.
    if (typeof count === 'number') setTotal(count);
    const rows = data || [];
    offsetRef.current = from + rows.length;
    setEvents(prev => reset ? rows : [...prev, ...rows]);
    // Derived from a short page rather than a separate hasMore flag.
    if (rows.length < PAGE) setExhausted(true);
  }, [buildListQuery, exhausted]);

  useEffect(() => {
    if (step !== 'list') return;
    loadPage(true);
    // loadPage is intentionally omitted: it changes identity whenever
    // `exhausted` flips, which would restart the list mid-scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, listSource, country, region, debouncedQuery]);

  // Endless scroll, on this screen only. There is no infinite scroll anywhere
  // else in the app, which is why it is a plain sentinel rather than a shared
  // hook nobody else would use.
  useEffect(() => {
    if (step !== 'list' || exhausted) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadPage(false);
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [step, exhausted, loadPage]);

  // ── search jumps straight to results ──────────────────────────────────────
  useEffect(() => {
    const myId = ++searchIdRef.current;
    if (!debouncedQuery.trim()) {
      // Only walk back if search is what put us on the list.
      if (searchIdRef.current === myId && listSource === 'search') {
        setListSource('region');
        setStep('locations');
      }
      return;
    }
    if (searchIdRef.current === myId) { setListSource('search'); setStep('list'); }
  }, [debouncedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── which RSVPs are already mine ──────────────────────────────────────────
  useEffect(() => {
    if (!userId || !events.length) return;
    (async () => {
      const ids = events.map(e => String(e.id));
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('profile_event_id, status')
        .eq('user_id', userId)
        .in('profile_event_id', ids);
      if (error) { console.error('EventsBrowser: could not read your RSVPs:', error.message); return; }
      setMyRsvps(prev => {
        const next = { ...prev };
        (data || []).forEach(r => { next[String(r.profile_event_id)] = r.status; });
        return next;
      });
    })();
  }, [userId, events]);

  /**
   * Going / Interested on an event that has no community post.
   *
   * The existing RSVP path keys on posts.id (Community.jsx:957-1023), and the
   * seeded and CSV-imported events have no post -- so there was nothing to hang
   * an RSVP on and these buttons could not have worked at all.
   *
   * event_rsvps already carries profile_event_id and a
   * UNIQUE (user_id, profile_event_id) that nothing used. This writes that side
   * of it. The post-keyed path is untouched and keeps working as it does.
   */
  async function rsvp(ev, status) {
    if (!userId) { alert('Sign in to save events.'); return; }
    if (rsvping) return;
    setRsvping(ev.id);
    const key  = String(ev.id);
    const prev = myRsvps[key] || null;
    // Optimistic, and rolled back on every failure path below.
    setMyRsvps(m => ({ ...m, [key]: status }));

    try {
      const { error: rsvpErr } = await supabase
        .from('event_rsvps')
        .upsert({ user_id: userId, post_id: null, profile_event_id: key, status },
                { onConflict: 'user_id,profile_event_id' });
      if (rsvpErr) throw rsvpErr;

      // The reader's own copy, so the event shows on their profile. It carries
      // source_event_id, so it is never counted as another event in that
      // location -- which is the whole reason that column exists.
      // Try the update first and look at WHICH ROWS came back, rather than
      // branching on whether an event_rsvps row already existed. Those two
      // facts can disagree: the older community RSVP path (Community.jsx:1018)
      // also writes profile_event_id, so a reader can hold an event_rsvps row
      // whose profile_events copy has no source_event_id, or none at all.
      //
      // Branching on `prev` sent that reader down the update arm, which
      // matched nothing and reported no error -- the button changed, the event
      // never reached their profile, and nothing said so. An update matching
      // zero rows is not an error in Postgres, so only the returned rows tell
      // you it did nothing.
      const { data: updated, error: roleErr } = await supabase.from('profile_events')
        .update({ role: status })
        .eq('user_id', userId)
        .eq('source_event_id', ev.id)
        .select('id');
      if (roleErr) throw roleErr;

      if (!updated?.length) {
        const { error: copyErr } = await supabase.from('profile_events').insert({
          user_id: userId,
          name: ev.name, date: ev.date, end_date: ev.end_date, time: ev.time,
          venue: ev.venue, address: ev.address, city: ev.city,
          region: ev.region, country: ev.country, country_code: ev.country_code,
          description: ev.description, ticket_link: ev.ticket_link,
          emoji: ev.emoji || '📅',
          color: status === 'going' ? 'var(--feedback-success)' : 'var(--neutral-500)',
          role: status,
          source_event_id: ev.id,
        });
        if (copyErr) throw copyErr;
      }
      onRsvpAdded?.();
    } catch (err) {
      setMyRsvps(m => ({ ...m, [key]: prev }));
      alert('Could not save that: ' + err.message);
    } finally {
      setRsvping(null);
    }
  }

  // ── back ──────────────────────────────────────────────────────────────────
  // One place, the way JournalEntryFlow computes its backTo. null means
  // render nothing, which keeps the corner empty on the first screen.
  const backTo =
    step === 'detail' ? 'list'
    : step === 'list'   ? (listSource === 'region' ? 'regions' : 'locations')
    : step === 'regions' ? 'locations'
    : null;

  function goBack() {
    if (backTo === 'locations' && listSource === 'search') setQuery('');
    if (backTo === 'list') setViewEvent(null);
    setStep(backTo);
  }

  const heading =
    step === 'locations' ? 'Bookish Events'
    : step === 'regions'  ? country?.country || 'Everywhere else'
    : step === 'list'     ? (listSource === 'search' ? 'Search results' : (region || country?.country || 'Everywhere else'))
    : viewEvent?.name || 'Event';

  const visibleCountries = showAllCountries ? countries : countries.slice(0, COUNTRY_CHIPS_SHOWN);
  const visibleRegions   = showAllRegions   ? regions   : regions.slice(0, REGIONS_SHOWN);

  const addBtn = (
    <button onClick={() => { onClose(); onHostEvent?.(); }} style={{
      width: '100%', padding: 'var(--space-10)', background: 'var(--brand-rust)',
      color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)',
      fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer',
    }}>+ Add or Host an Event</button>
  );

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Bookish events"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(41,37,36,0.55)',
        backdropFilter: 'blur(4px)', display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 20,
      }}>
      <div onClick={e => e.stopPropagation()} style={{
        position: 'relative', width: '100%', maxWidth: isMobile ? '100%' : 460,
        maxHeight: isMobile ? '90%' : '82vh', boxSizing: 'border-box',
        background: 'var(--brand-white)',
        borderRadius: isMobile ? '22px 22px 0 0' : 'var(--radius-xxl)',
        boxShadow: isMobile ? '0 -8px 30px rgba(41,37,36,0.28)' : '0 24px 60px rgba(41,37,36,0.28)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        // Keeps the bottom button clear of the home indicator on newer phones.
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
      }}>
        {isMobile && (
          <div style={{ width: 40, height: 4, background: '#d8ccb4', borderRadius: 'var(--radius-pill)', margin: '10px auto 0', flexShrink: 0 }} />
        )}

        {/* Header. The back arrow is the same 32px circle as JournalEntryFlow's
            (in ProfileBlocks.jsx) and mirrors the ✕ in the opposite corner --
            a tappable target rather than a small text link. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '14px 16px 10px', flexShrink: 0 }}>
          {backTo ? (
            <button onClick={goBack} aria-label="Back" style={{
              width: 32, height: 32, borderRadius: '50%', border: '1.5px solid rgba(139,115,85,.3)',
              background: '#fff', color: 'var(--neutral-500)', fontSize: 15, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>←</button>
          ) : <span style={{ width: 32, flexShrink: 0 }} />}

          <h3 style={{
            flex: 1, textAlign: 'center', margin: 0, fontFamily: 'var(--font-display)',
            fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--neutral-800)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{step === 'locations' ? '📍 ' : ''}{heading}</h3>

          <button onClick={onClose} aria-label="Close" style={{
            width: 32, height: 32, borderRadius: '50%', border: '1.5px solid rgba(139,115,85,.3)',
            background: '#fff', color: 'var(--neutral-500)', fontSize: 14, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px', scrollbarWidth: 'none' }}>

          {/* ══════ 1 · BROWSE BY LOCATION ══════ */}
          {step === 'locations' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', padding: '8px 12px', marginBottom: 'var(--space-md)' }}>
                <span style={{ fontSize: 'var(--font-size-base)', opacity: 0.4 }}>🔍</span>
                <input value={query} onChange={e => setQuery(e.target.value)}
                  placeholder="Search events or locations…"
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 'var(--font-size-base)', background: 'transparent', color: 'var(--neutral-800)' }} />
              </div>

              <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', margin: '0 0 8px' }}>Explore by location</p>

              {countriesLoad ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '20px 0', textAlign: 'center' }}>Loading locations…</p>
              ) : countriesErr ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', padding: '20px 0', textAlign: 'center' }}>
                  We could not load locations just now. Nothing is missing, try again in a moment.
                </p>
              ) : countries.length === 0 ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '20px 0', textAlign: 'center' }}>
                  No upcoming events anywhere yet. Be the first to host one.
                </p>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)' }}>
                    {visibleCountries.map(c => (
                      <button key={c.country || '__none__'} onClick={() => openCountry(c)} style={{
                        display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                        padding: '10px 12px', borderRadius: 'var(--radius-10)',
                        border: '1px solid var(--brand-sand)', background: 'var(--brand-white)',
                        cursor: 'pointer', textAlign: 'left', fontSize: 'var(--font-size-sm)',
                        color: 'var(--neutral-800)', fontFamily: 'var(--font-ui)',
                      }}>
                        <span style={{ fontSize: 'var(--font-size-lg)' }}>{flagEmoji(c.country_code) || '🌍'}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.country || 'Everywhere else'}
                        </span>
                      </button>
                    ))}
                  </div>
                  {countries.length > COUNTRY_CHIPS_SHOWN && (
                    <button onClick={() => setShowAllCountries(s => !s)} style={{
                      background: 'none', border: 'none', color: 'var(--brand-rust)', fontWeight: 700,
                      fontSize: 'var(--font-size-xs)', cursor: 'pointer', padding: '10px 0',
                    }}>
                      {showAllCountries ? 'Show fewer locations' : `See more locations (${countries.length - COUNTRY_CHIPS_SHOWN}) →`}
                    </button>
                  )}
                </>
              )}

              {popular.length > 0 && (
                <>
                  <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', margin: '16px 0 8px' }}>Popular this month</p>
                  {popular.map(ev => (
                    <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', padding: '8px 0', borderBottom: '1px solid var(--neutral-100)' }}>
                      <DateTile date={ev.date} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</p>
                        <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{placeLine(ev)}</p>
                      </div>
                      {/* One button here, not the pair — the lighter touch the
                          mockup asks for on this list. */}
                      <button onClick={() => rsvp(ev, 'going')} disabled={rsvping === ev.id}
                        style={{
                          flexShrink: 0, padding: '5px 12px', borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--font-size-tiny)', fontWeight: 700, cursor: 'pointer',
                          border: 'none', opacity: rsvping === ev.id ? 0.6 : 1,
                          background: myRsvps[String(ev.id)] === 'going' ? 'var(--feedback-success)' : 'var(--brand-rust)',
                          color: 'var(--brand-white)',
                        }}>
                        {myRsvps[String(ev.id)] === 'going' ? '✓ Going' : 'Going'}
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* ══════ 2 · CHOOSE LOCATION ══════ */}
          {step === 'regions' && (
            <>
              <div style={{ marginBottom: 'var(--space-md)' }}>{addBtn}</div>
              <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', margin: '0 0 4px' }}>Select a location</p>

              {regionsLoad ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '20px 0', textAlign: 'center' }}>Loading…</p>
              ) : regionsErr ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', padding: '20px 0', textAlign: 'center' }}>
                  We could not load these locations just now. Try again in a moment.
                </p>
              ) : (
                <>
                  <LocationRow
                    label={`All ${country?.country || 'Everywhere else'}`}
                    count={country?.n ?? ''}
                    onClick={() => { setRegion(null); setListSource('country'); setStep('list'); }}
                  />
                  {visibleRegions.map(r => (
                    <LocationRow key={r.region || '__none__'}
                      label={r.region || 'Elsewhere in this country'}
                      count={r.n}
                      onClick={() => { setRegion(r.region); setListSource('region'); setStep('list'); }}
                    />
                  ))}
                  {regions.length > REGIONS_SHOWN && (
                    <button onClick={() => setShowAllRegions(s => !s)} style={{
                      width: '100%', background: 'none', border: 'none', color: 'var(--neutral-600)',
                      fontWeight: 600, fontSize: 'var(--font-size-sm)', cursor: 'pointer', padding: '12px 0',
                    }}>
                      {showAllRegions ? 'Fewer ⌃' : `More locations ⌄`}
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {/* ══════ 3 · EVENT LIST ══════ */}
          {step === 'list' && (
            <>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 10px' }}>
                {/* Never "0 events" for a count that did not arrive. */}
                {total === null ? 'Loading…' : `${total} event${total === 1 ? '' : 's'}`}
              </p>

              {eventsErr ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--feedback-error)', padding: '20px 0', textAlign: 'center' }}>
                  We could not load these events just now. Try again in a moment.
                </p>
              ) : (!eventsLoad && events.length === 0) ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', padding: '30px 0', textAlign: 'center' }}>
                  {listSource === 'search' ? 'Nothing matched that.' : 'No upcoming events here yet.'}
                </p>
              ) : (
                <>
                  {events.map(ev => {
                    const mine = myRsvps[String(ev.id)];
                    const busy = rsvping === ev.id;
                    return (
                      <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-10)', padding: '12px 0', borderBottom: '1px solid var(--neutral-100)' }}>
                        <DateTile date={ev.date} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)', margin: 0 }}>{ev.name}</p>
                          <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '2px 0 6px' }}>{placeLine(ev)}</p>
                          <button onClick={() => { setViewEvent(ev); setStep('detail'); }} style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: 'var(--brand-rust)', fontWeight: 700, fontSize: 'var(--font-size-xxs)',
                          }}>View event →</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', flexShrink: 0 }}>
                          <button onClick={() => rsvp(ev, 'going')} disabled={busy} style={{
                            padding: '4px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-tiny)',
                            fontWeight: 700, cursor: 'pointer', border: 'none', opacity: busy ? 0.6 : 1,
                            background: mine === 'going' ? 'var(--feedback-success)' : 'var(--brand-rust)',
                            color: 'var(--brand-white)',
                          }}>{mine === 'going' ? '✓ Going' : 'Going'}</button>
                          <button onClick={() => rsvp(ev, 'interested')} disabled={busy} style={{
                            padding: '4px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-tiny)',
                            fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1,
                            border: `1px solid ${mine === 'interested' ? 'var(--neutral-600)' : 'var(--brand-sand)'}`,
                            background: 'var(--brand-white)',
                            color: mine === 'interested' ? 'var(--neutral-800)' : 'var(--neutral-600)',
                          }}>{mine === 'interested' ? '✓ Interested' : 'Interested'}</button>
                        </div>
                      </div>
                    );
                  })}
                  {!exhausted && <div ref={sentinelRef} style={{ height: 1 }} />}
                  {eventsLoad && (
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', textAlign: 'center', padding: '12px 0' }}>Loading…</p>
                  )}
                </>
              )}
            </>
          )}

          {/* ══════ 4 · EVENT DETAILS ══════ */}
          {step === 'detail' && viewEvent && (
            <div style={{ paddingBottom: 'var(--space-md)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                <DateTile date={viewEvent.date} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>{viewEvent.name}</p>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '2px 0 0' }}>
                    {formatEventDate(viewEvent.date, { weekday: 'long', month: 'long', day: 'numeric' })}
                    {viewEvent.time ? ` · ${viewEvent.time}` : ''}
                  </p>
                  {placeLine(viewEvent) && (
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '2px 0 0' }}>{placeLine(viewEvent)}</p>
                  )}
                </div>
              </div>

              {/* Each of these renders only when the event actually has one. A
                  missing price says nothing rather than saying "Free". */}
              {viewEvent.ticket_cost && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)', margin: '0 0 10px' }}>
                  <strong>Tickets:</strong> {viewEvent.ticket_cost}
                </p>
              )}
              {viewEvent.description && (
                <div style={{ padding: '12px 14px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-md)' }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: 0, lineHeight: 1.55 }}>{viewEvent.description}</p>
                </div>
              )}
              {(viewEvent.ticket_link || viewEvent.event_url) && (
                <a href={viewEvent.ticket_link || viewEvent.event_url} target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)',
                    padding: 'var(--space-10)', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
                    borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600,
                    color: 'var(--neutral-800)', textDecoration: 'none', marginBottom: 'var(--space-md)',
                  }}>
                  🎟️ Open event page <span style={{ color: 'var(--neutral-400)' }}>↗</span>
                </a>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button onClick={() => rsvp(viewEvent, 'going')} disabled={rsvping === viewEvent.id} style={{
                  flex: 1, padding: '11px', borderRadius: 'var(--radius-lg)', border: 'none', cursor: 'pointer',
                  fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--brand-white)',
                  background: myRsvps[String(viewEvent.id)] === 'going' ? 'var(--feedback-success)' : 'var(--brand-rust)',
                }}>{myRsvps[String(viewEvent.id)] === 'going' ? '✓ Going' : 'Going'}</button>
                <button onClick={() => rsvp(viewEvent, 'interested')} disabled={rsvping === viewEvent.id} style={{
                  flex: 1, padding: '11px', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                  fontSize: 'var(--font-size-base)', fontWeight: 600, background: 'var(--brand-white)',
                  border: `1px solid ${myRsvps[String(viewEvent.id)] === 'interested' ? 'var(--neutral-600)' : 'var(--brand-sand)'}`,
                  color: 'var(--neutral-800)',
                }}>{myRsvps[String(viewEvent.id)] === 'interested' ? '✓ Interested' : 'Interested'}</button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom on every screen except Choose Location, where it sits at the
            top instead because the list underneath it can be long. */}
        {step !== 'regions' && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--neutral-100)', flexShrink: 0 }}>
            {addBtn}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
